// external library imports
import React, { useState, useContext, useEffect } from 'react'
import { sumBy, isEmpty } from 'lodash-es'

// React components
import PriceGroup from './price-group'
import EventHeading from './event-heading'
import IllinoisOptions from './illinois-options'
import { Loading, CapacityMessage } from '../../shared/components'
import { ProductTable } from '../../carts/components/product-table'

// helper methods
import { setHitMax } from '../../shared/helpers/maximums'
import {
  extractPricing,
  hitSpecificConstituencyMax,
  ticketsWithConstituencyPricing
} from '../../shared/helpers/logic'
import {
  sortByAge,
  hitEmployeeMax,
  hitMembershipMax,
  hitManualUpdateMax,
  inManualUpdate,
  isChildLearning,
  anyMembershipTickets
} from '../helpers/logic'
import { includesEmployee } from '../helpers/logic'
import { errorMessages } from '../../shared/helpers/error-messages'

// React app contexts
import { TicketsAppContext } from '../context/tickets-app-context'
import { OldCartContext } from '../context/old-cart-context'

// API call method
import { getRemainingTickets } from '../../services/api'

const illinoisAreaTicket = ticket => {
  return illinoisAreaOption(ticket) && ticket.quantity !== 0
}

const illinoisAreaOption = ticket => {
  const fromChicago = ticket.label === 'Chicago Residents'
  const fromIllinois = ticket.label === 'Illinois Residents'
  return fromChicago || fromIllinois
}

const getAvailability = (
  dependency,
  chosenAddOn,
  chosenEvent,
  fromEvent,
  selectedDate
) => {
  let { availability, quantityLimit } = dependency ? chosenAddOn : chosenEvent

  if (fromEvent && !selectedDate) availability = Infinity

  if (!dependency && chosenEvent.chosenZone) {
    availability = chosenEvent.chosenZone.availableCount
  }

  if (quantityLimit && quantityLimit <= availability)
    return { availability: quantityLimit, limitType: 'EVENT_LIMIT' }
  else return { availability: availability, limitType: 'EVENT_CAPACITY' }
}

function Tickets({
  showEventHeading = true,
  cartItem: { tickets },
  illinois,
  chicago,
  illinoisChecked = false,
  noPrice = false,
  hidePricingColumn = false
}) {
  const [showIllinoisOptions, setShowIllinoisOptions] =
    useState(illinoisChecked)
  const [loading, setLoading] = useState(false)
  const [cantContinueConstTickets, setCantContinueConstTickets] =
    useState(false)
  const { manualMaximum } = useContext(OldCartContext)
  const {
    membership,
    chosenAddOn,
    chosenEvent,
    fromEvent,
    events: { loading: eventsLoading },
    selectedDate
  } = useContext(TicketsAppContext)

  const [remainingTickets, setRemainingTickets] = useState({
    remainingEmployeeTickets: null,
    remainingMembershipTickets: membership.adultMemberLimit,
    remainingConstituencyTickets: null
  })

  let hasIllinoisOptions = illinois || chicago

  // first get the target of this component - add-on or event?
  // if add-on, assign parent event to `dependency`
  const event = chosenAddOn ? chosenAddOn : chosenEvent
  const dependency = chosenAddOn ? chosenEvent : null

  // if an event's pricing object is null we know that it is a "scheduled event" so
  // we look in the times attribute to find the correct pricing object
  // if it's not null, check if it's zoned and if so, only look at the prices for that zone

  // SHEDDWEB-341: Adding this check to ensure that the correct pricing obj is used for events like 4D
  // that can have different price types active by the hour. ex: no IL resident pricing until after 5pm on IL resident free admission nights
  let useAddOnPricingZone = event?.id === chosenAddOn?.id ? chosenAddOn : null
  const pricing = extractPricing(event, useAddOnPricingZone)

  // destructuring the pricing object to pull out what we need for this component
  const { member, nonMember } = pricing

  // `availability` here is the number the frontend uses to limit ticket quantities
  // during the entire time the url says '/tickets'
  let { availability, limitType } = getAvailability(
    dependency,
    chosenAddOn,
    chosenEvent,
    fromEvent,
    selectedDate
  )

  const employeeMax = hitEmployeeMax(
    event,
    remainingTickets?.remainingEmployeeTickets
  )
  const membershipMax = hitMembershipMax(
    event,
    remainingTickets?.remainingMembershipTickets
  )

  const constituencyMax = hitSpecificConstituencyMax(
    event,
    remainingTickets?.remainingConstituencyTickets
  )

  // SHEDDWEB-225 - availability must be the manual maximum during manual update
  // this prevents issues where availability is 0 because the nonmember tickets are still
  // held in the Tessitura cart.
  if (inManualUpdate) availability = manualMaximum

  const hitMaximumsByAge = setHitMax(
    chosenAddOn && chosenAddOn.tickets,
    chosenEvent.tickets,
    membership,
    availability,
    isChildLearning(pricing.member),
    employeeMax,
    membershipMax,
    constituencyMax
  )

  const manualUpdateMax = hitManualUpdateMax(event, manualMaximum)

  const childLearning = member && isChildLearning(member)
  let showLearningChildMembershipOptions =
    childLearning && membership.childrenAllowed

  const ticketsWithMemberPricing = chosenEvent.tickets.filter(
    ticket => ticket.member
  )
  const ticketsWithConstituencyPricingIds = ticketsWithConstituencyPricing(
    chosenEvent.tickets
  ).map(t => t.priceTypeId)

  const showMemberOptions =
    membership.active &&
    member &&
    (!childLearning || showLearningChildMembershipOptions) &&
    !isEmpty(ticketsWithMemberPricing)

  const fetchRemainingTickets = async eventId =>
    (await ticketsWithConstituencyPricingIds.length) > 0
      ? getRemainingTickets(
          eventId,
          chosenEvent.productId,
          ticketsWithConstituencyPricingIds
        )
      : getRemainingTickets(eventId)

  useEffect(() => {
    if (!selectedDate) return
    let subscribed = true
    setLoading(true)

    fetchRemainingTickets(chosenEvent.id)
      .then(data => {
        if (subscribed) {
          setRemainingTickets(data)
          setLoading(false)
        }
      })
      .catch(e => {
        console.warn(e)
        setLoading(false)
      })
    if (chosenAddOn) {
      // set showIllinoisOptions to false if the add on has generic pricing
      let genericAddOn = Object.keys(chosenAddOn?.pricing['nonMember']).some(
        type => type.includes('generic')
      )
      if (genericAddOn) {
        setShowIllinoisOptions(false)
      } else {
        setShowIllinoisOptions(
          (chosenAddOn?.addOnOnly &&
            chosenEvent.tickets.some(ticket => illinoisAreaTicket(ticket))) ||
            illinoisChecked
        )
      }
    }
    return () => (subscribed = false)
  }, [])

  useEffect(() => {
    let employeeTickets = includesEmployee(tickets)
    let constituencyTickets = ticketsWithConstituencyPricing(tickets)?.filter(
      set => set.quantity > 0
    )
    setCantContinueConstTickets(
      (constituencyTickets.length > 0 && employeeTickets) ||
        constituencyTickets.length > 1
    )
  }, [constituencyMax, employeeMax])

  const renderPriceGroup = (group, isMember) => {
    // sorting prices to display oldest to youngest age ranges
    const ageSortedGroup = sortByAge(group)

    return ageSortedGroup.map((priceType, index) => (
      <PriceGroup
        key={index}
        priceType={priceType}
        last={index === group.length - 1}
        illinois={isMember ? false : illinois}
        showIllinoisOptions={showIllinoisOptions && !isMember}
        hitMaximumsByAge={hitMaximumsByAge}
        noPrice={noPrice}
        hidePricingColumn={hidePricingColumn}
        remainingTickets={remainingTickets}
      />
    ))
  }

  const toggleIllinoisOptions = () => {
    setShowIllinoisOptions(!showIllinoisOptions)
  }

  if (eventsLoading || loading) {
    return <Loading />
  }

  // we check the _overall_ quantity of tickets selected here
  const currentQuantity = sumBy(tickets, ticket => ticket.quantity)
  const shouldRenderMaxMessage =
    currentQuantity === availability && !inManualUpdate

  const eventCapacityMessage =
    'Event at capacity. You have reserved all the remaining tickets for the selected event.'
  const eventLimitMessage =
    'You have reserved the maximum tickets allowed for this event. Please call 312-939-2438 to inquire about additional tickets.'

  let message
  if (!selectedDate || limitType === 'EVENT_LIMIT') message = eventLimitMessage
  else message = eventCapacityMessage

  const ticketMaxMessage = (
    <p className="font-bold text-16 no-tickets-message">{message}</p>
  )

  return (
    <div data-testid="ticket-select">
      {showEventHeading && (
        <EventHeading
          event={event}
          show={showEventHeading}
        />
      )}

      {/* if the membership is active we need to render the member price types */}
      {showMemberOptions && (
        <div data-testid="member-tickets-section" className="mb-16">
          <h2>Member Tickets</h2>
          <p>
            A card holder must be present on the day of the visit to receive
            member benefits
          </p>

          {shouldRenderMaxMessage && ticketMaxMessage}

          <CapacityMessage
            event={event}
            remainingTickets={remainingTickets}
            hasActiveMembership={membership.active}
            manualMaximum={manualUpdateMax && manualMaximum}
            showMembershipMessaging={true}
          />

          {inManualUpdate && !anyMembershipTickets(event.tickets) && (
            <p className="font-bold text-16">
              Please add at least 1 member ticket to continue
            </p>
          )}
          <table className="product-table" data-testid="tickets-product-table">
            <tbody>{renderPriceGroup(member, true)}</tbody>
          </table>
        </div>
      )}
      <div>
        {showMemberOptions && nonMember && (
          <React.Fragment>
            <h2>Additional Visitors (Non-Members)</h2>
            <p>You will be charged for additional tickets</p>
          </React.Fragment>
        )}

        {nonMember && (
          <IllinoisOptions
            illinois={illinois}
            illinoisChecked={showIllinoisOptions}
            show={chosenAddOn ? false : hasIllinoisOptions}
            toggle={toggleIllinoisOptions}
          />
        )}

        {noPrice && (
          <p>
            In order to receive your Illinois resident pricing, please re-enter
            your party and select all Illinois residents.
          </p>
        )}

        {!showMemberOptions && !nonMember && (
          <div className="font-bold text-16 no-tickets-message">
            The event you have chosen is only available to Shedd Aquarium
            Members. Please <a href="/accounts/sign_in">login</a> or{' '}
            <a href="/membership_plans">purchase a membership</a> to view
            available tickets. Having trouble logging in? Please{' '}
            <a href="/accounts/password/new">
              click here to reset your password
            </a>{' '}
            or call the Membership Hotline at 312-692-3284.
          </div>
        )}

        {shouldRenderMaxMessage && ticketMaxMessage}

        {cantContinueConstTickets && (
          <p className="font-bold text-16">
            {errorMessages.constituencyTicketsCantContinue}
          </p>
        )}

        {nonMember && (
          <CapacityMessage
            event={event}
            remainingTickets={remainingTickets}
            hasActiveMembership={membership.active}
            manualMaximum={manualUpdateMax && manualMaximum}
            showMembershipMessaging={false}
          />
        )}

        {nonMember && (
          <ProductTable>{renderPriceGroup(nonMember, false)}</ProductTable>
        )}
      </div>
    </div>
  )
}

export default Tickets
